import { FLabel } from '@components/form/FLabel';
import { MultiComboBoxInput } from '@components/inputs/ComboBox';
import { type DropdownOptions } from '@components/inputs/SelectInput/MultiSelectInput/MultiSelectInput';
import { Modal } from '@components/modal';
import {
  useAddMeterHierarchy,
  useFetchRelatableMetersInfinite,
} from 'api/assets/assetsServices';
import { useDebounce } from 'hooks/useDebounce';
import { t } from 'i18next';
import { type Disclosure } from 'interfaces/disclosure/Disclosure.interface';
import { type FC, useEffect, useMemo, useState } from 'react';

interface HierarchyModalProps extends Disclosure {
  meterId?: number;
  type?: 'children' | 'parents';
}

const HierarchyModal: FC<HierarchyModalProps> = ({
  isOpen,
  handleClose,
  handleOpen,
  meterId,
  type,
}) => {
  const [relatableQuery, setRelatableQuery] = useState('');
  const [relatableMeter, setRelatableMeter] = useState<number | undefined>();

  const debouncedQuery = useDebounce(relatableQuery, 300);

  const {
    data: relatableMetersData,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchRelatableMetersInfinite({
    id: meterId,
    query: debouncedQuery,
    relatable: type as string,
  });

  const { mutate, isPending, isSuccess } = useAddMeterHierarchy();

  const parentOptions: DropdownOptions[] = useMemo(() => {
    if (relatableMetersData?.pages) {
      return relatableMetersData.pages.flatMap(
        (page) =>
          page?.meters?.map((meter: any) => ({
            value: meter.id,
            name: meter.name,
          })) || []
      );
    }
    return [];
  }, [relatableMetersData?.pages]);

  const handleChange = (id: number): void => {
    setRelatableMeter(id);
  };

  const handleSubmit = (): void => {
    const body = {
      parent_id: type === 'parents' ? relatableMeter : meterId,
      child_id: type === 'children' ? relatableMeter : meterId,
    };
    mutate(body);
  };

  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [isSuccess]);

  return (
    <Modal
      title={'Hierarchy'}
      isOpen={isOpen}
      onClose={handleClose}
      onOpen={handleOpen}
      actionBtns={{
        primary: {
          label: t('common.buttons.confirm'),
          onClick: handleSubmit,
          isLoading: isPending,
        },
        secondary: { label: t('common.buttons.cancel') },
      }}
      width={600}
    >
      <div className="flex flex-col gap-2 min-h-[215px]">
        <FLabel id={'relatable-dropdown'}>
          {type === 'parents'
            ? t('assetsPage.meterDetailsModal.parentMeter')
            : t('assetsPage.meterDetailsModal.childrenMeter')}
        </FLabel>
        <MultiComboBoxInput
          id={'relatable-dropdown'}
          options={parentOptions}
          query={relatableQuery}
          handleQuery={setRelatableQuery}
          isFetching={isFetching}
          multiple={false}
          placeholder={t('assetsPage.placeholders.meterDescription')}
          maxHeight={200}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onChange={(option: DropdownOptions) => {
            if (option?.value && typeof option?.value === 'number') {
              handleChange(option.value);
            }
          }}
        />
      </div>
    </Modal>
  );
};
export default HierarchyModal;
